'use client';

import { useState, useEffect, useContext } from 'react';
import { supabase } from '../../supabaseClient';
import { AuthContext } from '../../context/AuthContext';
import { RadioGroup } from '@headlessui/react';
import { CheckCircleIcon, MagnifyingGlassIcon, BuildingOffice2Icon } from '@heroicons/react/20/solid';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import TeamSettings from './TeamSettings';
import PendingInvite from './PendingInvites';


export default function TeamPage() {
  const { user } = useContext(AuthContext);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true); // Loading state for the organization fetch

  useEffect(() => {
    if (!user) return;

    const fetchOrganization = async () => {
      try {
        const { data, error } = await supabase
          .from('organization_members')
          .select('organizations(id, name)')
          .eq('user_id', user.id)
          .single();

        if (error && error.code !== 'PGRST116') {
          console.error('Error fetching organization:', error);
        } else {
          setOrganization(data?.organizations || null);
        }
      } catch (err) {
        console.error('Error fetching organization:', err);
      } finally {
        setLoading(false); // Stop loading once the fetch is complete
      }
    };

    fetchOrganization();
  }, [user]);

  if (loading) {
    // Skeleton loader while fetching organization
    return (
      <div>
      </div>
    );
  }

  return (
    
    <div>
      <div className="mt-6">
      <PendingInvite userId={user.id} />
        {organization ? (
          <TeamSettings organizationId={organization.id} />
        ) : (
          <JoinOrCreateCompany user={user} />
        )}
      </div>
    </div>
  );
}

function JoinOrCreateCompany({ user }) {
  const [selectedOption, setSelectedOption] = useState('join'); // 'join' or 'create'

  const options = [
    { id: 'join', title: 'Join an Existing Team', description: 'Get the Team Key from the Owner and enter to join.' },
    { id: 'create', title: 'Create a New Team', description: 'Start a Team to unlock full features.' },
  ];

  return (
    <div>
      {/* <div className="border-l-4 border-yellow-400 bg-yellow-50 p-4 my-4">
        <div className="flex">
          <div className="shrink-0">
            <ExclamationTriangleIcon aria-hidden="true" className="size-5 text-yellow-400" />
          </div>
          <div className="ml-3">
            <p className="text-sm text-yellow-700">
              You are not a member of a Company. Please join an existing company or create a new one.
            </p>
          </div>
        </div>
      </div> */}
      <RadioGroup
        value={selectedOption}
        onChange={setSelectedOption}
        className="mt-6 grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-4"
      >
        {options.map((option) => (
          <RadioGroup.Option
            key={option.id}
            value={option.id}
            className={({ checked }) =>
              `group relative flex cursor-pointer rounded-lg border bg-white p-4 shadow-sm focus:outline-none ${
                checked ? 'border-yellow-600 ring-1 ring-yellow-600' : 'border-gray-300'
              }`
            }
          >
            {({ checked }) => (
              <>
                <span className="flex flex-1">
                  <span className="flex flex-col">
                    <span className="block text-sm font-medium text-gray-900">{option.title}</span>
                    <span className="mt-1 text-sm text-gray-500">{option.description}</span>
                  </span>
                </span>
                <CheckCircleIcon
                  className={`size-5 ${
                    checked ? 'text-yellow-600' : 'invisible'
                  }`}
                  aria-hidden="true"
                />
                <span
                  aria-hidden="true"
                  className={`pointer-events-none absolute -inset-px rounded-lg ${
                    checked ? 'border-2 border-yellow-600' : 'border-2 border-transparent'
                  }`}
                />
              </>
            )}
          </RadioGroup.Option>
        ))}
      </RadioGroup>

      <div className="mt-6">
        {selectedOption === 'join' && <JoinCompany user={user} />}
        {selectedOption === 'create' && <CreateCompany user={user} />}
      </div>
    </div>
  );
}


function JoinCompany({ user }) {
  const [searchKey, setSearchKey] = useState('');
  const [organizationOptions, setOrganizationOptions] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSearchKeyChange = async (key) => {
    setSearchKey(key);
    setSelectedOrganization(null);
    setErrorMessage('');
    setSuccessMessage('');

    if (key.trim() === '') {
      setOrganizationOptions([]);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('organizations')
        .select('id, name')
        .eq('organization_key', key);

      if (error) {
        throw error;
      }

      setOrganizationOptions(data || []);
    } catch (err) {
      console.error('Error searching for organization:', err.message);
      setErrorMessage('Failed to search for organizations.');
    }
  };

  const handleJoinCompany = async () => {
  if (!selectedOrganization) {
    setErrorMessage('Please select an organization to join.');
    return;
  }

  setLoading(true);
  setErrorMessage('');
  setSuccessMessage('');

  try {
    // Step 1: Fetch team usage data using Supabase edge function
    const { data: teamUsage, error: fetchError } = await supabase.functions.invoke('get-team-usage', {
      method: 'POST',
      body: { organizationId: selectedOrganization.id },
    });

    if (fetchError) {
      throw new Error(`Failed to fetch team usage data: ${fetchError.message}`);
    }

    // Step 2: Check if spots are available
    if (teamUsage.remainingSpots <= 0) {
      setErrorMessage(`No available spots in ${selectedOrganization.name}. Please contact the admin.`);
      setLoading(false);
      return;
    }

    // Step 3: Proceed with joining the company
    const { error } = await supabase
      .from('organization_members')
      .insert([{ user_id: user.id, organization_id: selectedOrganization.id, role: 'member' }]);

    if (error) throw error;

    setSuccessMessage(`Successfully joined the organization: ${selectedOrganization.name}`);

    // Reload page to reflect changes
    window.location.reload();
    
    // Reset state
    setSearchKey('');
    setOrganizationOptions([]);
    setSelectedOrganization(null);
  } catch (err) {
    setErrorMessage(err.message || 'Failed to join the organization.');
  } finally {
    setLoading(false);
  }
};
  

  return (
    <div className="bg-white p-6 sm:p-8 rounded-lg border border-gray-200 shadow-sm max-w-3xl mx-auto">

  {/* Header Section */}
  <div className="mb-6">
    <h2 className="text-xl font-bold text-gray-900">Join an Existing Team</h2>
    <p className="mt-2 text-sm text-gray-600">
      To join a Team, enter the unique invitation key provided by your team administrator to search for your team.  Your administrator can find this key in the Team Settings page.  They can also invite you directly via email.
    </p>
  </div>

  <div className="my-6 border-t border-gray-200" />

  {/* Step-based UI: Shows search first, then confirmation */}
  {!selectedOrganization ? (
    /* --- STEP 1: Search for the Workspace --- */
    <div>
      <label htmlFor="team-key-search" className="block text-sm font-medium leading-6 text-gray-900">
        Team Key
      </label>
      <div className="relative mt-2">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
        </div>
        <input
          id="team-key-search"
          type="text"
          placeholder="Enter key to search for your Team..."
          value={searchKey}
          onChange={(e) => handleSearchKeyChange(e.target.value)}
          className="block w-full max-w-md rounded-md border-0 py-2 pl-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-yellow-600 sm:text-sm sm:leading-6"
        />
        {/* Dropdown search results */}
        {organizationOptions.length > 0 && (
          <div className="absolute z-10 mt-1 w-full max-w-md rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5">
            <ul className="max-h-60 overflow-auto py-1 text-base focus:outline-none sm:text-sm">
              {organizationOptions.map((org) => (
                <li
                  key={org.id}
                  onClick={() => { handleSearchKeyChange(''); setSelectedOrganization(org); }}
                  className="relative cursor-pointer select-none py-2 px-4 text-gray-900 hover:bg-gray-100"
                >
                  {org.name}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  ) : (
    /* --- STEP 2: Confirm Selection and Join --- */
    <div>
      <h3 className="text-base font-semibold text-gray-800">Confirm Your Team</h3>
      <div className="mt-4 flex items-center gap-4 rounded-lg border border-yellow-300 bg-yellow-50 p-4">
        <div className="flex-shrink-0">
          <BuildingOffice2Icon className="h-8 w-8 text-yellow-600" aria-hidden="true" />
        </div>
        <div className="flex-1">
          <p className="text-sm text-gray-600">You are about to send a request to join:</p>
          <p className="text-lg font-bold text-gray-900">{selectedOrganization.name}</p>
        </div>
      </div>
      <div className="mt-6 flex items-center gap-3">
        <button
          onClick={handleJoinCompany}
          className="rounded-md bg-yellow-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-yellow-500 disabled:opacity-60"
          disabled={loading}
        >
          {loading ? 'Sending Request...' : 'Request to Join'}
        </button>
        <button
          onClick={() => setSelectedOrganization(null)}
          className="rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
        >
          Search Again
        </button>
      </div>
    </div>
  )}

  <div className="my-8 border-t border-gray-200" />

  {/* "What Happens Next" Explainer Section */}
  <div>
    <h3 className="text-base font-semibold text-gray-800">Upon approval from an admin, you will:</h3>
    <ul className="mt-4 space-y-4 text-sm">
      <li className="flex gap-x-3">
        <CheckCircleIcon className="mt-0.5 h-5 w-5 flex-none text-green-500" aria-hidden="true" />
        <span className="text-gray-600">
          <strong className="font-semibold text-gray-900">Become a Team Member.</strong> You'll be added to the Team and can start collaborating immediately.
        </span>
      </li>
      <li className="flex gap-x-3">
        <CheckCircleIcon className="mt-0.5 h-5 w-5 flex-none text-green-500" aria-hidden="true" />
        <span className="text-gray-600">
          <strong className="font-semibold text-gray-900">Access Shared Resources.</strong> Gain access to all projects and data shared within the team, based on the permissions set by your admin.
        </span>
      </li>
    </ul>
  </div>

  {/* Status Messages */}
  <div className="mt-6">
    {successMessage && <p className="text-sm text-green-600">{successMessage}</p>}
    {errorMessage && <p className="text-sm text-red-600">{errorMessage}</p>}
  </div>

</div>
  );
}

function CreateCompany({ user }) {
  const [companyName, setCompanyName] = useState('');
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [profile, setProfile] = useState({ first_name: '', last_name: '' }); // Profile state
  const { session } = useContext(AuthContext); // Get session from AuthContext


  useEffect(() => {
    const fetchProfile = async () => {
      if (user) {
        const { data, error } = await supabase
          .from('profile')
          .select('first_name, last_name')
          .eq('id', user.id)
          .single();

        if (!error && data) {
          setProfile({ first_name: data.first_name, last_name: data.last_name });
        } else {
          console.error('Error fetching profile:', error);
        }
      }
    };

    fetchProfile();
  }, [user]);

  const handleCreateCompany = async () => {
    setLoading(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      // Call the new integrated edge function
      const response = await fetch('https://zccgzqkkpmqpawelftbw.supabase.co/functions/v1/create-company', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          // Include authorization if needed
          'Authorization': `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({
          user_id: user.id,
          company_name: companyName
          // Note: we don't need to pass profile info as the edge function will fetch it
        }),
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || result.details || 'Failed to create team');
      }

      setSuccessMessage('Team created successfully');
      setCompanyName('');
      // Reload page to reflect changes
      window.location.reload();
    } catch (err) {
      setErrorMessage(err.message || 'Failed to create team.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white p-6 sm:p-8 rounded-lg border border-gray-200 shadow-sm max-w-3xl mx-auto">
  
  {/* Header Section */}
  <div className="mb-6">
    <h2 className="text-xl font-bold text-gray-900">Create Your Team Workspace</h2>
    <p className="mt-2 text-sm text-gray-600">
      Creating a Team will begin your 14-day, all-access free trial. No credit card is required to start.
    </p>
  </div>
  
  {/* Form Section */}
  <div>
    <label htmlFor="team-name" className="block text-sm font-medium leading-6 text-gray-900">
      Team Name
    </label>
    <div className="mt-2 flex flex-col sm:flex-row sm:items-center gap-3">
      <input
        id="team-name"
        type="text"
        placeholder="e.g., Acme Engineering"
        value={companyName}
        onChange={(e) => setCompanyName(e.target.value)}
        className="block w-full max-w-md rounded-md border-0 py-2 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-yellow-600 sm:text-sm sm:leading-6"
      />
      <button
        onClick={handleCreateCompany}
        className="flex-shrink-0 rounded-md bg-yellow-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-yellow-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow-600 disabled:opacity-60 disabled:cursor-wait"
        disabled={loading}
      >
        {loading ? 'Creating...' : 'Create & Start Trial'}
      </button>
    </div>
  </div>

  <div className="my-8 border-t border-gray-200" />

  {/* Explainer Section */}
  <div>
    <h3 className="text-base font-semibold text-gray-800">Here’s what creating a Team unlocks:</h3>
    <ul className="mt-4 space-y-4 text-sm">
      <li className="flex gap-x-3">
        <CheckCircleIcon className="mt-0.5 h-5 w-5 flex-none text-green-500" aria-hidden="true" />
        <span className="text-gray-600">
          <strong className="font-semibold text-gray-900">Administrator Role.</strong> You will be the administrator, with full permissions to manage members, projects, and billing. You can assign additional admins later.
        </span>
      </li>
      <li className="flex gap-x-3">
        <CheckCircleIcon className="mt-0.5 h-5 w-5 flex-none text-green-500" aria-hidden="true" />
        <span className="text-gray-600">
          <strong className="font-semibold text-gray-900">Unlimited Collaboration.</strong> Invite as many team members as you need. They can upload data, contribute to projects, and view results.
        </span>
      </li>
      <li className="flex gap-x-3">
        <CheckCircleIcon className="mt-0.5 h-5 w-5 flex-none text-green-500" aria-hidden="true" />
        <span className="text-gray-600">
          <strong className="font-semibold text-gray-900">Full Feature Access.</strong> During your 14-day trial, you and your team will have access to every feature on the platform, including advanced analysis and HQ Mode.
        </span>
      </li>
    </ul>
  </div>
  
  {/* Status Messages */}
  <div className="mt-6">
    {successMessage && <p className="text-sm text-green-600">{successMessage}</p>}
    {errorMessage && <p className="text-sm text-red-600">{errorMessage}</p>}
  </div>

</div>
  );
}
